import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button, Collapse, Input } from 'src/components/ui';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { get } from 'lodash';
import classnames from 'src/util/classnames';
import CustomBadge from './CustomBadge';
import useWrapInput from './customFields/utils/useWrapInput';
import Icon from 'src/components/Icon';

function CollapsableTreeSelect(props) {
  const {
    emptyText,
    placeholder,
    filteredRootNodes,
    flatSearchResults,
    valueNode,
    onSelect,
    onSelectAndClose,
    collapsed,
    setCollapsed,
    collapsedAncestorId,
    expandToSelected,
    isOpen,
    setOpen,
    toggle,
    disabled,
    searchText,
    setSearchText,
    isSelectable,
    value,
  } = useCollapsableTreeSelectHooks(props);

  const { prefix, sufix, fieldId, fieldSchema, formContext, searchable = true, infoBadge } = props;
  const toggleRef = useRef(null);

  const valueBreadcrumb = useMemo(() => {
    if (!valueNode) return null;
    const labels = getAncestorLabels(valueNode);
    return labels.length > 0 ? labels.join(' ⇒ ') : null;
  }, [valueNode]);

  const dropdownEl = (
    <>
      <div className="collapsable-tree-select input-wrapper" ref={toggleRef}>
        <div
          className={classnames('form-control tree-select-toggle', { disabled, open: isOpen })}
          onClick={disabled ? undefined : toggle}
          role="combobox"
          aria-expanded={isOpen}
        >
          <span className="tree-select-value">
            <NodeLabel node={valueNode} emptyText={emptyText} placeholder={placeholder} />
          </span>
        </div>
        {isOpen && (
          <TreeSelectMenu toggleRef={toggleRef} onClose={() => setOpen(false)}>
            {searchable && (
              <div className="tree-select-search" onMouseDown={e => e.stopPropagation()}>
                <Input autoFocus bsSize="sm" placeholder={t`Search…`} value={searchText} onChange={e => setSearchText(e.target.value)} />
              </div>
            )}
            <div className="tree-select-items">
              {searchText ? (
                !flatSearchResults?.length ? (
                  <div className="tree-select-empty">
                    <Trans>No results</Trans>
                  </div>
                ) : (
                  flatSearchResults.map(node => (
                    <FlatNode
                      key={node.id}
                      node={node}
                      selectedId={value}
                      onSelect={onSelect}
                      onSelectAndClose={onSelectAndClose}
                      isSelectable={isSelectable}
                      infoBadge={infoBadge}
                    />
                  ))
                )
              ) : !filteredRootNodes?.length ? (
                <div className="tree-select-empty">{emptyText || <Trans>No options</Trans>}</div>
              ) : (
                filteredRootNodes.map(node => (
                  <TreeNode
                    key={node.id}
                    node={node}
                    selectedId={value}
                    selectedLabel={valueNode?.labelNoDepth}
                    collapsed={collapsed}
                    setCollapsed={setCollapsed}
                    collapsedAncestorId={collapsedAncestorId}
                    expandToSelected={expandToSelected}
                    onSelect={onSelect}
                    onSelectAndClose={onSelectAndClose}
                    isSelectable={isSelectable}
                    infoBadge={infoBadge}
                  />
                ))
              )}
            </div>
          </TreeSelectMenu>
        )}
      </div>
      {valueBreadcrumb && (
        <div className="tree-select-path-hint" title={valueBreadcrumb}>
          {valueBreadcrumb}
        </div>
      )}
    </>
  );

  return useWrapInput({
    input: dropdownEl,
    prefix,
    sufix,
    fieldId: fieldId || props.id,
    fieldSchema,
    formContext,
    formData: props.value,
  });
}

function TreeSelectMenu({ toggleRef, onClose, children }) {
  const menuRef = useRef(null);
  // position: fixed from the start — keeps the portal out of document flow so it
  // can't trigger scrollbars or grid reflows that corrupt the first measurement.
  // left: -9999 keeps it offscreen until updatePosition places it correctly.
  const [style, setStyle] = useState({ position: 'fixed', visibility: 'hidden', top: 0, left: -9999 });

  const updatePosition = useCallback(() => {
    if (!toggleRef.current) return;
    const rect = toggleRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const menuHeight = menuRef.current?.offsetHeight ?? 340;
    const openUpward = spaceBelow < menuHeight && spaceAbove > spaceBelow;

    setStyle({
      position: 'fixed',
      left: rect.left,
      width: rect.width,
      minWidth: Math.max(rect.width, 220),
      zIndex: 9999,
      ...(openUpward ? { bottom: window.innerHeight - rect.top, top: 'auto' } : { top: rect.bottom + 2, bottom: 'auto' }),
      visibility: 'visible',
    });
  }, [toggleRef]);

  useEffect(() => {
    // rAF defers measurement until after the browser's layout pass so any
    // concurrent effects (collapsed-state init, form reflows, autoFocus scroll)
    // have settled before we read getBoundingClientRect().
    const rafId = requestAnimationFrame(updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [updatePosition]);

  useEffect(() => {
    function handleMouseDown(e) {
      if (menuRef.current && !menuRef.current.contains(e.target) && toggleRef.current && !toggleRef.current.contains(e.target)) {
        onClose();
      }
    }
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [onClose, toggleRef]);

  useEffect(() => {
    function handleKeyDown(e) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  return createPortal(
    <div ref={menuRef} className="tree-select-menu" style={style}>
      {children}
    </div>,
    document.body
  );
}

function useCollapsableTreeSelectHooks({
  name,
  value,
  emptyText,
  options,
  parentId,
  id,
  label,
  rootNodes: propRootNodes,
  disabled,
  onChange,
  defaultCollapseLevel,
  selectableTypes,
  excludeTypes,
  selectableNames,
  filterPath,
  placeholder,
}) {
  const [isOpen, setOpen] = useState(false);
  const [searchText, setSearchText] = useState('');

  const toggle = useCallback(() => setOpen(v => !v), []);

  useEffect(() => {
    if (!isOpen) setSearchText('');
  }, [isOpen]);

  const rootNodes = useMemo(() => {
    if (propRootNodes) return propRootNodes;
    if (!options || !parentId) return null;

    let items = options;
    if (filterPath) {
      items = items.filter(item => item.path?.includes(filterPath));
    }

    const tsOptions = items.map(item => makeTreeNode(item, id, label, parentId));
    const roots = organizeTreeNodes(tsOptions);
    visitTrees(roots);
    return roots;
  }, [propRootNodes, options, parentId, id, label, filterPath]);

  const filteredRootNodes = useMemo(() => {
    if (!propRootNodes || !filterPath) return rootNodes;
    const allFlat = [];
    forEachTreeNodeIn(rootNodes, n => allFlat.push(n));
    const filtered = allFlat.filter(n => n.item?.path?.includes(filterPath));
    const clones = filtered.map(n => ({
      id: n.id,
      label: n.labelNoDepth ?? n.label,
      parentId: n.parentId,
      visitIdx: 0,
      depth: 0,
      item: n.item,
    }));
    const rebuilt = organizeTreeNodes(clones);
    visitTrees(rebuilt);
    return rebuilt;
  }, [propRootNodes, filterPath, rootNodes]);

  const allFlatNodes = useMemo(() => {
    const nodes = [];
    forEachTreeNodeIn(filteredRootNodes ?? rootNodes, node => nodes.push(node));
    return nodes;
  }, [filteredRootNodes, rootNodes]);

  const nodesById = useMemo(() => {
    const map = {};
    allFlatNodes.forEach(n => {
      map[n.id] = n;
    });
    return map;
  }, [allFlatNodes]);

  const valueNode = nodesById[value];

  const isSelectable = useCallback(
    node => {
      if (selectableNames?.length) return selectableNames.includes(node.item?.name);
      const typeName = node.item?.organizationalUnitType?.name;
      if (excludeTypes?.length && excludeTypes.includes(typeName)) return false;
      if (selectableTypes?.length) return selectableTypes.includes(typeName);
      return true;
    },
    [selectableNames, selectableTypes, excludeTypes]
  );

  const onSelect = useCallback(
    id => {
      onChange({ target: { name, value: id } });
    },
    [name, onChange]
  );

  const onSelectAndClose = useCallback(
    id => {
      onChange({ target: { name, value: id } });
      setOpen(false);
    },
    [name, onChange]
  );

  const flatSearchResults = useMemo(() => {
    if (!searchText) return null;
    const lower = searchText.toLowerCase();
    return allFlatNodes.filter(n => n.labelNoDepth?.toLowerCase().includes(lower));
  }, [searchText, allFlatNodes]);

  const [collapsed, _setCollapsed] = useState();
  const setCollapsed = useCallback((path, val) => _setCollapsed(prev => ({ ...(prev || {}), [path]: val })), []);

  useEffect(() => {
    if (rootNodes) {
      const initial = {};
      if (defaultCollapseLevel) {
        forEachTreeNodeIn(rootNodes, node => {
          initial[node.path] = node.depth >= defaultCollapseLevel;
        });
      }
      _setCollapsed(initial);
    }
  }, [rootNodes, defaultCollapseLevel]);

  // ID of the shallowest visible ancestor that has the selected node in a collapsed subtree
  const collapsedAncestorId = useMemo(() => {
    if (!valueNode) return null;
    const chain = [];
    let cur = valueNode.parent;
    while (cur) {
      chain.unshift(cur);
      cur = cur.parent;
    }
    for (const ancestor of chain) {
      if (collapsed?.[ancestor.path]) return ancestor.id;
    }
    return null;
  }, [valueNode, collapsed]);

  // Expand all collapsed ancestors up to (and including) the one containing the selected node
  const expandToSelected = useCallback(() => {
    if (!valueNode) return;
    const updates = {};
    let cur = valueNode.parent;
    while (cur) {
      if (collapsed?.[cur.path]) updates[cur.path] = false;
      cur = cur.parent;
    }
    if (Object.keys(updates).length > 0) {
      _setCollapsed(prev => ({ ...(prev || {}), ...updates }));
    }
  }, [valueNode, collapsed]);

  return {
    emptyText,
    filteredRootNodes: filteredRootNodes ?? rootNodes,
    flatSearchResults,
    valueNode,
    placeholder,
    disabled,
    onSelect,
    onSelectAndClose,
    isSelectable,
    collapsed,
    setCollapsed,
    collapsedAncestorId,
    expandToSelected,
    isOpen,
    setOpen,
    toggle,
    searchText,
    setSearchText,
    value,
  };
}

export function makeTreeNode(item, idAttr, labelAttr, parentIdAttr) {
  return {
    id: item[idAttr],
    label: item[labelAttr],
    parentId: item[parentIdAttr],
    visitIdx: 0,
    depth: 0,
    item,
  };
}

export function organizeTreeNodes(nodesList) {
  const nodeMap = nodesList.reduce((acc, n) => {
    acc[n.id] = n;
    return acc;
  }, {});
  const rootNodes = [];
  nodesList.forEach(node => {
    const parent = node.parentId ? nodeMap[node.parentId] : null;
    if (parent) {
      node.parent = parent;
      if (!parent.children) parent.children = [];
      parent.children.push(node);
    } else {
      rootNodes.push(node);
    }
  });
  return rootNodes;
}

export function visitTrees(rootNodes) {
  const stack = rootNodes.map(node => ['', 0, '', node]);
  let visitIdx = 0;

  while (stack.length) {
    const [parentPath, depth, depthPrefix, current] = stack.shift();
    visitIdx += 1;

    if (!current.visitIdx) {
      const pathPrefix = parentPath ? `${parentPath}/` : '';
      current.depth = depth;
      current.depthPrefix = depthPrefix;
      current.labelNoDepth = current.label;
      current.label = `${depthPrefix}${current.label}`;
      current.path = `${pathPrefix}${current.id}`;
      current.visitIdx = visitIdx;

      if (current.children) {
        current.children[0].isFirst = true;
        current.children[current.children.length - 1].isLast = true;
        stack.unshift(...current.children.map(child => [current.path, depth + 1, '  '.repeat(depth + 1), child]));
      }
    }
  }
}

function forEachTreeNodeIn(rootNodes, fn) {
  const stack = [...(rootNodes || [])];
  while (stack.length) {
    const current = stack.shift();
    fn(current);
    if (current.children) stack.unshift(...current.children);
  }
}

function getAncestorLabels(node) {
  const labels = [];
  let cur = node.parent;
  while (cur) {
    labels.unshift(cur.labelNoDepth);
    cur = cur.parent;
  }
  return labels;
}

function NodeInfoBadge({ node, infoBadge }) {
  if (!infoBadge?.path) return null;
  const value = get(node.item, infoBadge.path);
  if (value === null || value === undefined || (typeof value !== 'string' && typeof value !== 'number')) return null;
  const color = infoBadge.colors?.[value] ?? 'secondary';
  return (
    <CustomBadge color={color} className="tree-node-info-badge">
      {value}
    </CustomBadge>
  );
}

function TreeNode({
  node,
  selectedId,
  selectedLabel,
  collapsed,
  setCollapsed,
  collapsedAncestorId,
  expandToSelected,
  onSelect,
  onSelectAndClose,
  isSelectable,
  infoBadge,
}) {
  const { id, path, children } = node;
  const hasChildren = !!children?.length;
  const isCollapsed = collapsed?.[path];
  const selectable = isSelectable(node);
  const isSelected = id === selectedId;
  const isCollapsedAncestor = id === collapsedAncestorId;

  const onToggle = useCallback(
    e => {
      e.stopPropagation();
      setCollapsed(path, !isCollapsed);
    },
    [setCollapsed, path, isCollapsed]
  );

  // Row click: toggle expand/collapse if has children, otherwise select (no close)
  const onRowClick = useCallback(() => {
    if (hasChildren) {
      setCollapsed(path, !isCollapsed);
    } else if (selectable) {
      onSelect(id);
    }
  }, [hasChildren, selectable, onSelect, setCollapsed, path, isCollapsed, id]);

  return (
    <>
      <div
        className={classnames('tree-node', {
          'tree-node-selectable': selectable,
          'tree-node-disabled': !selectable,
          'tree-node-selected': isSelected,
        })}
        style={{ paddingLeft: `${node.depth * 16 + 6}px` }}
        onClick={onRowClick}
      >
        <Button
          type="button"
          color="link"
          className="tree-node-toggle-btn"
          onClick={onToggle}
          style={{ visibility: hasChildren ? 'visible' : 'hidden' }}
          tabIndex={-1}
        >
          <Icon icon={isCollapsed ? 'Plus' : 'Minus'} weight="regular" />
        </Button>
        <div className="tree-node-label-group">
          <span className="tree-node-label" title={node.labelNoDepth}>
            {node.labelNoDepth}
          </span>
          <NodeInfoBadge node={node} infoBadge={infoBadge} />
        </div>
        {selectable ? (
          <Button
            type="button"
            size="sm"
            color={isSelected ? 'success' : 'outline-secondary'}
            className="tree-node-select-btn"
            onClick={e => {
              e.stopPropagation();
              onSelectAndClose(id);
            }}
          >
            {isSelected ? <Trans>Selected</Trans> : <Trans>Select</Trans>}
          </Button>
        ) : (
          <span className="tree-node-unselectable-badge">—</span>
        )}
      </div>
      {isCollapsedAncestor && (
        <div className="tree-node-selected-indicator" style={{ paddingLeft: `${node.depth * 16 + 30}px` }} onClick={e => e.stopPropagation()}>
          <Icon icon="CheckCircle" />
          <span className="tree-node-selected-indicator-label">
            <Trans>Selected:</Trans> {selectedLabel}
          </span>
          <Button type="button" size="sm" color="link" className="tree-node-expand-btn" onClick={expandToSelected}>
            <Trans>Expand to selection</Trans>
          </Button>
        </div>
      )}
      {hasChildren && (
        <Collapse isOpen={!isCollapsed}>
          {children.map(child => (
            <TreeNode
              key={child.id}
              node={child}
              selectedId={selectedId}
              selectedLabel={selectedLabel}
              collapsed={collapsed}
              setCollapsed={setCollapsed}
              collapsedAncestorId={collapsedAncestorId}
              expandToSelected={expandToSelected}
              onSelect={onSelect}
              onSelectAndClose={onSelectAndClose}
              isSelectable={isSelectable}
              infoBadge={infoBadge}
            />
          ))}
        </Collapse>
      )}
    </>
  );
}

function FlatNode({ node, selectedId, onSelect, onSelectAndClose, isSelectable, infoBadge }) {
  const selectable = isSelectable(node);
  const isSelected = node.id === selectedId;
  const ancestors = getAncestorLabels(node);

  const onRowClick = useCallback(() => {
    if (selectable) onSelect(node.id);
  }, [selectable, onSelect, node.id]);

  return (
    <div
      className={classnames('tree-node tree-node-flat', {
        'tree-node-selectable': selectable,
        'tree-node-disabled': !selectable,
        'tree-node-selected': isSelected,
      })}
      onClick={onRowClick}
    >
      <div className="tree-node-flat-content">
        <div className="tree-node-label-group">
          <span className="tree-node-label" title={node.labelNoDepth}>
            {node.labelNoDepth}
          </span>
          <NodeInfoBadge node={node} infoBadge={infoBadge} />
        </div>
        {ancestors.length > 0 && (
          <span className="tree-node-breadcrumb" title={ancestors.join(' › ')}>
            {ancestors.join(' › ')}
          </span>
        )}
      </div>
      {selectable ? (
        <Button
          type="button"
          size="sm"
          color={isSelected ? 'success' : 'outline-secondary'}
          className="tree-node-select-btn"
          onClick={e => {
            e.stopPropagation();
            onSelectAndClose(node.id);
          }}
        >
          {isSelected ? <Trans>Selected</Trans> : <Trans>Select</Trans>}
        </Button>
      ) : (
        <span className="tree-node-unselectable-badge">—</span>
      )}
    </div>
  );
}

function NodeLabel({ node, emptyText, placeholder }) {
  const text = node?.labelNoDepth ?? emptyText ?? placeholder;
  if (!text) return null;
  return (
    <span className={classnames('label', { 'as-placeholder': !!!node?.labelNoDepth })} title={text}>
      {text}
    </span>
  );
}

export default CollapsableTreeSelect;
